import { useSyncExternalStore } from "react";
import type { CartLine } from "../cart";
import type { Product, ShopConfig } from "../shopApi";
import { formatCents } from "../shopApi";

// Small shared state for the storefront: the "added to cart" toast, kept outside
// React so any page can show it, plus price and delivery helpers.

function createStore<T>(initial: T) {
  let value = initial;
  const listeners = new Set<() => void>();
  return {
    get: () => value,
    set(next: T) {
      value = next;
      listeners.forEach((l) => l());
    },
    subscribe(l: () => void) {
      listeners.add(l);
      return () => {
        listeners.delete(l);
      };
    },
  };
}

export interface AddedToast {
  id: number;
  name: string;
}

const toastStore = createStore<AddedToast | null>(null);
let toastTimer: ReturnType<typeof setTimeout> | undefined;

export const addedToast = {
  show(name: string) {
    clearTimeout(toastTimer);
    toastStore.set({ id: Date.now(), name });
    toastTimer = setTimeout(() => toastStore.set(null), 4000);
  },
  hide() {
    clearTimeout(toastTimer);
    toastStore.set(null);
  },
};

export function useAddedToast(): AddedToast | null {
  return useSyncExternalStore(toastStore.subscribe, toastStore.get, () => null);
}

export function unitPrice(product: Product, variantId?: string): number {
  const variant = product.variants?.find((v) => v.id === variantId);
  return product.price_cents + (variant?.price_delta_cents ?? 0);
}

/** "€4", or "from €15" when sizes cost different amounts. */
export function cardPrice(product: Product): string {
  const variants = product.variants ?? [];
  if (variants.length < 2) return formatCents(unitPrice(product, variants[0]?.id), product.currency);
  const prices = variants.map((v) => product.price_cents + v.price_delta_cents);
  const min = Math.min(...prices);
  const label = formatCents(min, product.currency);
  return Math.max(...prices) === min ? label : `from ${label}`;
}

const METHOD_NAMES: Record<string, string> = { card: "card", cod: "cash on delivery" };

export function deliveryFacts(config: ShopConfig | null): string[] {
  if (!config) return [];
  const facts: string[] = [];
  const { fee_cents, free_above_cents } = config.delivery;
  facts.push(fee_cents > 0 ? `delivery ${formatCents(fee_cents, config.currency)}` : "free delivery");
  if (fee_cents > 0 && free_above_cents) facts.push(`free over ${formatCents(free_above_cents, config.currency)}`);
  const methods = config.payment_methods.map((m) => METHOD_NAMES[m] ?? m);
  if (methods.length) facts.push(`pay by ${methods.join(" or ")}`);
  return facts;
}

export function cartSubtotal(lines: CartLine[], products: Product[]): number {
  return lines.reduce((sum, line) => {
    const product = products.find((p) => p.id === line.productId);
    return product ? sum + unitPrice(product, line.variantId) * line.quantity : sum;
  }, 0);
}
